import React, { useEffect, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Select, DatePicker, Radio, Checkbox, InputNumber, message } from 'antd';
import './mcp.css';
import { authHeader } from '../../../utils/api';
import McpHeader from '../../../components/mcp/McpHeader';

const { TextArea } = Input;

// Inline SVG icons (lucide-style)
const ArrowLeft = ({ size = 16, strokeWidth = 1.75 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
);
const ArrowRight = ({ size = 16, strokeWidth = 1.75 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
);
const X = ({ size = 14, strokeWidth = 1.75 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
);

const CHAPTERS = ['Audience', 'Basics', 'Targeting', 'Budget', 'Creative', 'Publish'];

const STEP_COPY = {
  audience: { headline: "Here's who we're building for", subtitle: "Review the audience you configured on the dashboard. Tweak it there if anything's off." },
  'name-page': { headline: 'Name it and pick your Page', subtitle: 'The name is internal; the Page is what customers see.' },
  'conversion-objective': { headline: 'What should this campaign do?', subtitle: 'Where customers convert decides which goals Meta allows.' },
  categories: { headline: 'Does your ad fall into a regulated category?', subtitle: "Meta requires a declaration for a few industries. Most local service ads don't qualify." },
  'advantage-demographics': { headline: 'Who should Meta look for?', subtitle: 'Your pins decide where. This decides who.' },
  'schedule-budget': { headline: 'Set your spend', subtitle: 'You can change or pause this any time in Ads Manager.' },
  frequency: { headline: 'Cap how often one person sees it?', subtitle: 'Optional. Most operators skip this.' },
  'destination-cta': { headline: 'Where should the click land?', subtitle: 'Your website, with the button that matches what you want them to do.' },
  format: { headline: 'Choose your ad format', subtitle: 'Image ads show a single photo; carousel ads rotate through multiple cards.' },
  images: { headline: 'Add your images', subtitle: 'Real trucks and real jobs outperform stock photos.' },
  carousel: { headline: 'Add your carousel cards', subtitle: 'Each card shows its own image. 2–10 cards, one headline and link for all.' },
  video: { headline: 'Add your videos', subtitle: 'Upload and Meta processes them — this can take a few minutes.' },
  'text-variants': { headline: 'Write the words', subtitle: 'Meta mixes and matches variants to find what works per person.' },
  preview: { headline: "Here's your ad", subtitle: "This is Meta's own render of your ad — the same thing customers will see." },
  'review-publish': { headline: 'Review everything, then ship it', subtitle: 'Nothing spends until you publish.' },
};

const WIZARD_STEPS = [
  { id: 'audience', chapter: 'Audience', title: 'Your audience' },
  { id: 'name-page', chapter: 'Basics', title: 'Name & Page' },
  { id: 'conversion-objective', chapter: 'Basics', title: 'Goal' },
  { id: 'categories', chapter: 'Basics', title: 'Special ad categories' },
  { id: 'advantage-demographics', chapter: 'Targeting', title: 'Targeting' },
  { id: 'schedule-budget', chapter: 'Budget', title: 'Schedule & budget' },
  { id: 'frequency', chapter: 'Budget', title: 'Frequency' },
  { id: 'destination-cta', chapter: 'Creative', title: 'Destination & CTA' },
  { id: 'format', chapter: 'Creative', title: 'Ad format' },
  { id: 'images', chapter: 'Creative', title: 'Images' },
  { id: 'text-variants', chapter: 'Creative', title: 'Ad text' },
  { id: 'preview', chapter: 'Publish', title: 'Preview' },
  { id: 'review-publish', chapter: 'Publish', title: 'Review & publish' },
];

const initialState = {
  campaign: {
    campaignName: '',
    pageId: '',
    conversionLocation: 'WEBSITE',
    objective: 'OUTCOME_TRAFFIC',
    accountSlot: null,
    specialAdCategories: [],
    sacCountry: '',
    demographics: { ageMin: 18, ageMax: 65 },
    advantagePlus: { placements: true },
    placements: [],
    schedule: { startsAt: null, endsAt: null },
    budget: { type: 'daily', amountUsd: 10 },
    frequencyCap: null,
    destinationUrl: '',
    cta: 'LEARN_MORE',
    adFormat: 'image',
    images: [],
    videos: [],
    headlines: [''],
    primaryTexts: [''],
  },
  stepIndex: 0,
  maxVisitedIndex: 0,
};

function reducer(state, action) {
  switch (action.type) {
    case 'set_campaign':
      return { ...state, campaign: { ...state.campaign, ...action.campaign } };
    case 'next': {
      const maxIndex = WIZARD_STEPS.length - 1;
      const stepIndex = Math.min(state.stepIndex + 1, maxIndex);
      return { ...state, stepIndex, maxVisitedIndex: Math.max(state.maxVisitedIndex, stepIndex) };
    }
    case 'back':
      return { ...state, stepIndex: Math.max(state.stepIndex - 1, 0) };
    case 'goto':
      if (action.stepIndex < 0 || action.stepIndex > state.maxVisitedIndex) return state;
      return { ...state, stepIndex: action.stepIndex };
    default:
      return state;
  }
}

const labelCls = 'block text-xs font-bold uppercase tracking-[0.14em] text-ink-200 mb-2';

function isValidWebUrl(value) {
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol);
  } catch {
    return false;
  }
}

// Per-step validation, run when Next is clicked. Returns { fieldKey: message } (empty = OK).
const STEP_VALIDATORS = {
  'name-page': (c) => ({
    ...(!c.campaignName?.trim() && { campaignName: 'Campaign name is required' }),
    ...(!c.pageId && { pageId: 'Select a Facebook Page' }),
  }),
  'conversion-objective': (c) => ({
    ...(!c.conversionLocation && { conversionLocation: 'Choose where customers should convert' }),
    ...(!c.objective && { objective: 'Choose a campaign objective' }),
    ...(c.objective === 'OUTCOME_LEADS' && { objective: 'Leads campaigns are not supported yet — choose Traffic or Engagement' }),
  }),
  categories: (c) => ({
    ...((c.specialAdCategories || []).length > 0 && !c.sacCountry && { sacCountry: 'Select the service-area country' }),
  }),
  'advantage-demographics': (c) => ({
    ...(!c.demographics?.ageMin && { ageMin: 'Minimum age is required' }),
    ...(!c.demographics?.ageMax && { ageMax: 'Maximum age is required' }),
    ...(c.demographics?.ageMin && c.demographics?.ageMax && c.demographics.ageMin > c.demographics.ageMax && { ageMax: 'Maximum age must be at least the minimum age' }),
  }),
  'schedule-budget': (c) => ({
    ...(!c.budget?.type && { budgetType: 'Choose a budget type' }),
    ...((!c.budget?.amountUsd || c.budget.amountUsd < 1) && { budgetAmount: 'Budget must be at least $1' }),
    ...(c.budget?.type === 'lifetime' && !c.schedule?.endsAt && { endsAt: 'An end date is required for a lifetime budget' }),
    ...(c.schedule?.startsAt && c.schedule?.endsAt && !c.schedule.endsAt.isAfter(c.schedule.startsAt) && { endsAt: 'End date must be after the start date' }),
  }),
  'destination-cta': (c) => ({
    ...(!c.destinationUrl?.trim()
      ? { destinationUrl: 'Destination URL is required' }
      : !isValidWebUrl(c.destinationUrl.trim()) && { destinationUrl: 'Enter a full web address, e.g. https://your-site.com' }),
    ...(!c.cta && { cta: 'Choose a call-to-action button' }),
  }),
  format: (c) => ({
    ...(!c.adFormat && { adFormat: 'Choose an ad format' }),
  }),
  'text-variants': (c) => ({
    ...(!(c.headlines || []).some(h => h.trim()) && { headlines: 'At least one headline is required' }),
    ...(!(c.primaryTexts || []).some(p => p.trim()) && { primaryTexts: 'At least one primary text is required' }),
  }),
};

function validateStep(stepId, campaign) {
  return STEP_VALIDATORS[stepId] ? STEP_VALIDATORS[stepId](campaign) : {};
}

// Final safety net on Publish (steps are already validated on Next) — returns a list of messages
function getPublishErrors(c, connections) {
  const errors = [];
  if (!connections.facebook) errors.push('Connect a Facebook account in MCP Connections');
  WIZARD_STEPS.forEach(s => {
    Object.values(validateStep(s.id, c)).forEach(msg => errors.push(`${msg} (${s.title})`));
  });
  return errors;
}

const Required = () => <span className="text-ember"> *</span>;

const FieldError = ({ msg }) => (msg ? <p className="mt-1.5 text-xs font-semibold text-ember">{msg}</p> : null);

function StepContent({ step, campaign, setCampaign, errors = {}, connections, facebookPages, pagesLoading, publishing, publishResult, publishError, onPublish }) {
  switch (step.id) {
    case 'audience':
      return (
        <div className="space-y-4">
          <div className="rounded-2xl border border-paper-300 bg-paper-50 p-6">
            <div className="font-display text-base font-bold text-ink">Audience from dashboard</div>
            <p className="mt-2 text-sm text-ink-200">
              This campaign will target the stops and routes you selected on the MCP map. {campaign._stopCount || 0} stops, {campaign._routeCount || 0} routes.
            </p>
          </div>
          <div className="rounded-2xl border border-paper-300 bg-paper-50 p-6">
            <div className="text-xs font-bold uppercase tracking-[0.14em] text-ink-200">Radius</div>
            <div className="mt-2 text-sm text-ink">{campaign._radiusMi || 1} mile pins around each stop</div>
          </div>
        </div>
      );
    case 'name-page':
      return (
        <div className="space-y-5">
          <div>
            <label className={labelCls}>Campaign name<Required /></label>
            <Input
              status={errors.campaignName ? 'error' : undefined}
              value={campaign.campaignName}
              onChange={e => setCampaign({ campaignName: e.target.value })}
              placeholder="e.g. Spring install radius — TN"
              className="rounded-2xl"
              size="large"
            />
            <FieldError msg={errors.campaignName} />
          </div>
          <div>
            <label className={labelCls}>Facebook Page<Required /></label>
            {/* Real Pages from the connected Facebook account(s). The Page also decides which
                connected account — and so which ad account — the campaign is published to. */}
            <Select
              status={errors.pageId ? 'error' : undefined}
              value={campaign.pageId || undefined}
              onChange={v => setCampaign({ pageId: v, accountSlot: facebookPages.find(p => p.id === v)?.account_slot ?? null })}
              placeholder={pagesLoading ? 'Loading pages…' : 'Select your Page'}
              loading={pagesLoading}
              className="w-full"
              size="large"
              notFoundContent={pagesLoading ? 'Loading…' : 'No Pages found on your connected Facebook account'}
              options={facebookPages.map(p => ({
                value: p.id,
                label: p.account_name ? `${p.name} — ${p.account_name}` : p.name,
              }))}
            />
            <FieldError msg={errors.pageId} />
            {!pagesLoading && facebookPages.length === 0 && (
              <p className="mt-2 text-xs text-ember">
                No Facebook Pages found. Connect a Facebook account that manages a Page in{' '}
                <a href="/client/mcp/connections" className="font-semibold text-brand hover:underline">MCP Connections</a>.
              </p>
            )}
          </div>
        </div>
      );
    case 'conversion-objective':
      return (
        <div className="space-y-5">
          <div>
            <label className={labelCls}>Where should customers convert?<Required /></label>
            <Radio.Group
              value={campaign.conversionLocation}
              onChange={e => setCampaign({ conversionLocation: e.target.value })}
              className="flex flex-col gap-3"
            >
              <Radio value="WEBSITE">Website — send them to a landing page</Radio>
              <Radio value="CALL">Call — dial your business directly</Radio>
              <Radio value="MESSENGER">Messenger — open a chat</Radio>
              <Radio value="LEAD_FORM">Instant Form — collect leads in-app</Radio>
            </Radio.Group>
            <FieldError msg={errors.conversionLocation} />
          </div>
          <div>
            <label className={labelCls}>Campaign objective<Required /></label>
            <Select
              status={errors.objective ? 'error' : undefined}
              value={campaign.objective}
              onChange={v => setCampaign({ objective: v })}
              className="w-full"
              size="large"
              // Meta's objective names are OUTCOME_* (no S) — "OUTCOMES_*" was rejected with "(#100) Objective is invalid"
              options={[
                { value: 'OUTCOME_TRAFFIC', label: 'Traffic — send people to your site' },
                { value: 'OUTCOME_ENGAGEMENT', label: 'Engagement — get messages & calls' },
                { value: 'OUTCOME_LEADS', label: 'Leads — collect lead info (coming soon)', disabled: true },
              ]}
            />
            <FieldError msg={errors.objective} />
          </div>
        </div>
      );
    case 'categories':
      return (
        <div className="space-y-5">
          <div>
            <label className={labelCls}>Special ad categories</label>
            <Checkbox.Group
              value={campaign.specialAdCategories}
              onChange={v => setCampaign({ specialAdCategories: v })}
              className="flex flex-col gap-3"
            >
              <Checkbox value="HOUSING">Housing</Checkbox>
              <Checkbox value="EMPLOYMENT">Employment</Checkbox>
              <Checkbox value="CREDIT">Credit</Checkbox>
              <Checkbox value="ISSUES_ELECTIONS_POLITICS">Politics / Elections</Checkbox>
            </Checkbox.Group>
          </div>
          {(campaign.specialAdCategories || []).length > 0 && (
            <div>
              <label className={labelCls}>Service-area country<Required /></label>
              <Select
                status={errors.sacCountry ? 'error' : undefined}
                value={campaign.sacCountry || undefined}
                onChange={v => setCampaign({ sacCountry: v })}
                placeholder="Select country"
                className="w-full"
                size="large"
                options={[
                  { value: 'US', label: 'United States' },
                  { value: 'CA', label: 'Canada' },
                ]}
              />
              <FieldError msg={errors.sacCountry} />
            </div>
          )}
        </div>
      );
    case 'advantage-demographics':
      return (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Minimum age<Required /></label>
              <InputNumber
                status={errors.ageMin ? 'error' : undefined}
                value={campaign.demographics?.ageMin}
                onChange={v => setCampaign({ demographics: { ...campaign.demographics, ageMin: v } })}
                min={13}
                max={65}
                className="w-full"
                size="large"
              />
              <FieldError msg={errors.ageMin} />
            </div>
            <div>
              <label className={labelCls}>Maximum age<Required /></label>
              <InputNumber
                status={errors.ageMax ? 'error' : undefined}
                value={campaign.demographics?.ageMax}
                onChange={v => setCampaign({ demographics: { ...campaign.demographics, ageMax: v } })}
                min={18}
                max={65}
                className="w-full"
                size="large"
              />
              <FieldError msg={errors.ageMax} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Placements</label>
            <Radio.Group
              value={campaign.advantagePlus?.placements ? 'auto' : 'manual'}
              onChange={e => setCampaign({ advantagePlus: { placements: e.target.value === 'auto' } })}
              className="flex flex-col gap-3"
            >
              <Radio value="auto">Advantage+ placements (recommended)</Radio>
              <Radio value="manual">Manual placements</Radio>
            </Radio.Group>
          </div>
        </div>
      );
    case 'schedule-budget':
      return (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Start date</label>
              <DatePicker
                value={campaign.schedule?.startsAt}
                onChange={v => setCampaign({ schedule: { ...campaign.schedule, startsAt: v } })}
                className="w-full"
                size="large"
              />
            </div>
            <div>
              <label className={labelCls}>
                {campaign.budget?.type === 'lifetime' ? <>End date<Required /></> : 'End date (optional)'}
              </label>
              <DatePicker
                status={errors.endsAt ? 'error' : undefined}
                value={campaign.schedule?.endsAt}
                onChange={v => setCampaign({ schedule: { ...campaign.schedule, endsAt: v } })}
                className="w-full"
                size="large"
              />
              <FieldError msg={errors.endsAt} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Budget type<Required /></label>
            <Radio.Group
              value={campaign.budget?.type}
              onChange={e => setCampaign({ budget: { ...campaign.budget, type: e.target.value } })}
              className="flex gap-4"
            >
              <Radio value="daily">Daily</Radio>
              <Radio value="lifetime">Lifetime</Radio>
            </Radio.Group>
            <FieldError msg={errors.budgetType} />
          </div>
          <div>
            <label className={labelCls}>{campaign.budget?.type === 'lifetime' ? 'Lifetime budget' : 'Daily budget'} (USD)<Required /></label>
            <InputNumber
              status={errors.budgetAmount ? 'error' : undefined}
              value={campaign.budget?.amountUsd}
              onChange={v => setCampaign({ budget: { ...campaign.budget, amountUsd: v } })}
              min={1}
              prefix="$"
              className="w-full"
              size="large"
            />
            <FieldError msg={errors.budgetAmount} />
          </div>
        </div>
      );
    case 'frequency':
      return (
        <div className="space-y-5">
          <div>
            <label className={labelCls}>Frequency cap (optional)</label>
            <p className="mb-3 text-sm text-ink-200">Limit how many times one person sees this ad per week. Leave empty for no cap.</p>
            <InputNumber
              value={campaign.frequencyCap}
              onChange={v => setCampaign({ frequencyCap: v })}
              min={1}
              max={50}
              placeholder="No cap"
              className="w-full"
              size="large"
            />
          </div>
        </div>
      );
    case 'destination-cta':
      return (
        <div className="space-y-5">
          <div>
            <label className={labelCls}>Destination URL<Required /></label>
            <Input
              status={errors.destinationUrl ? 'error' : undefined}
              value={campaign.destinationUrl}
              onChange={e => setCampaign({ destinationUrl: e.target.value })}
              placeholder="https://your-site.com/landing"
              className="rounded-2xl"
              size="large"
            />
            <FieldError msg={errors.destinationUrl} />
          </div>
          <div>
            <label className={labelCls}>Call-to-action button<Required /></label>
            <Select
              status={errors.cta ? 'error' : undefined}
              value={campaign.cta}
              onChange={v => setCampaign({ cta: v })}
              className="w-full"
              size="large"
              options={[
                { value: 'LEARN_MORE', label: 'Learn More' },
                { value: 'SHOP_NOW', label: 'Shop Now' },
                { value: 'SIGN_UP', label: 'Sign Up' },
                { value: 'CONTACT_US', label: 'Contact Us' },
                { value: 'GET_QUOTE', label: 'Get Quote' },
                { value: 'BOOK_NOW', label: 'Book Now' },
              ]}
            />
            <FieldError msg={errors.cta} />
          </div>
        </div>
      );
    case 'format':
      return (
        <div className="space-y-5">
          <div>
            <label className={labelCls}>Ad format<Required /></label>
            <Radio.Group
              value={campaign.adFormat}
              onChange={e => setCampaign({ adFormat: e.target.value })}
              className="flex flex-col gap-3"
            >
              <Radio value="image">Single image</Radio>
              <Radio value="carousel">Carousel (2–10 cards)</Radio>
              <Radio value="video">Video</Radio>
            </Radio.Group>
            <FieldError msg={errors.adFormat} />
          </div>
        </div>
      );
    case 'images':
      return (
        <div className="space-y-5">
          <div>
            <label className={labelCls}>Upload images</label>
            <p className="mb-3 text-sm text-ink-200">Upload at least one image. Real trucks and real jobs outperform stock photos.</p>
            <div className="rounded-2xl border-2 border-dashed border-paper-300 bg-paper-50 px-6 py-12 text-center">
              <div className="text-sm font-semibold text-ink-200">Drag & drop or click to upload</div>
              <div className="mt-1 text-xs text-ink-200">PNG, JPG · recommended 1080×1080</div>
            </div>
          </div>
        </div>
      );
    case 'text-variants':
      return (
        <div className="space-y-5">
          <div>
            <label className={labelCls}>Headlines<Required /></label>
            {campaign.headlines?.map((h, i) => (
              <Input
                key={i}
                value={h}
                onChange={e => {
                  const headlines = [...campaign.headlines];
                  headlines[i] = e.target.value;
                  setCampaign({ headlines });
                }}
                status={errors.headlines && !h.trim() ? 'error' : undefined}
                placeholder={`Headline ${i + 1}`}
                className="rounded-2xl mb-2"
                size="large"
              />
            ))}
            <button
              type="button"
              onClick={() => setCampaign({ headlines: [...(campaign.headlines || []), ''] })}
              className="text-xs font-semibold text-brand hover:underline"
            >
              + Add headline variant
            </button>
            <FieldError msg={errors.headlines} />
          </div>
          <div>
            <label className={labelCls}>Primary text<Required /></label>
            {campaign.primaryTexts?.map((p, i) => (
              <TextArea
                key={i}
                value={p}
                onChange={e => {
                  const primaryTexts = [...campaign.primaryTexts];
                  primaryTexts[i] = e.target.value;
                  setCampaign({ primaryTexts });
                }}
                status={errors.primaryTexts && !p.trim() ? 'error' : undefined}
                placeholder={`Primary text ${i + 1}`}
                className="rounded-2xl mb-2"
                rows={3}
              />
            ))}
            <button
              type="button"
              onClick={() => setCampaign({ primaryTexts: [...(campaign.primaryTexts || []), ''] })}
              className="text-xs font-semibold text-brand hover:underline"
            >
              + Add text variant
            </button>
            <FieldError msg={errors.primaryTexts} />
          </div>
        </div>
      );
    case 'preview':
      return (
        <div className="space-y-5">
          <div className="rounded-2xl border border-paper-300 bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.14em] text-ink-200 mb-3">Ad preview</div>
            <div className="rounded-2xl border border-paper-200 bg-paper-50 p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-10 w-10 rounded-full bg-paper-300" />
                <div>
                  <div className="text-sm font-bold text-ink">Your Page Name</div>
                  <div className="text-xs text-ink-200">Sponsored</div>
                </div>
              </div>
              <div className="text-sm font-bold text-ink">{campaign.headlines?.[0] || 'Your headline here'}</div>
              <div className="mt-1 text-sm text-ink-100">{campaign.primaryTexts?.[0] || 'Your primary text here'}</div>
              <div className="mt-3 h-48 rounded-xl bg-paper-200 flex items-center justify-center text-sm text-ink-200">
                {campaign.adFormat === 'video' ? 'Video preview' : 'Image preview'}
              </div>
              <div className="mt-3 inline-flex rounded-full bg-paper-200 px-4 py-1.5 text-xs font-bold text-ink">
                {campaign.cta?.replace(/_/g, ' ') || 'Learn More'}
              </div>
            </div>
          </div>
        </div>
      );
    case 'review-publish':
      return (
        <div className="space-y-5">
          <div className="rounded-2xl border border-paper-300 bg-paper-50 p-6">
            <div className="font-display text-base font-bold text-ink mb-4">Review your campaign</div>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-ink-200">Name</dt><dd className="font-semibold text-ink">{campaign.campaignName || '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-ink-200">Conversion</dt><dd className="font-semibold text-ink">{campaign.conversionLocation}</dd></div>
              <div className="flex justify-between"><dt className="text-ink-200">Objective</dt><dd className="font-semibold text-ink">{campaign.objective?.replace(/_/g, ' ')}</dd></div>
              <div className="flex justify-between"><dt className="text-ink-200">Budget</dt><dd className="font-semibold text-ink">${campaign.budget?.amountUsd} {campaign.budget?.type}</dd></div>
              <div className="flex justify-between"><dt className="text-ink-200">Format</dt><dd className="font-semibold text-ink">{campaign.adFormat}</dd></div>
              <div className="flex justify-between"><dt className="text-ink-200">CTA</dt><dd className="font-semibold text-ink">{campaign.cta?.replace(/_/g, ' ')}</dd></div>
            </dl>
          </div>

          {/* Connection status */}
          <div className="rounded-2xl border border-paper-300 bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.14em] text-ink-200 mb-3">Connections</div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${connections.facebook ? 'bg-sage' : 'bg-ember'}`} />
                  <span className="text-sm font-semibold text-ink">Facebook / Meta</span>
                </div>
                <span className={`text-xs font-bold ${connections.facebook ? 'text-sage' : 'text-ember'}`}>
                  {connections.facebook ? 'Connected' : 'Not connected'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${connections.google ? 'bg-sage' : 'bg-ember'}`} />
                  <span className="text-sm font-semibold text-ink">Google Ads</span>
                </div>
                <span className={`text-xs font-bold ${connections.google ? 'text-sage' : 'text-ember'}`}>
                  {connections.google ? 'Connected' : 'Not connected'}
                </span>
              </div>
            </div>
            {!connections.facebook && (
              <div className="mt-4 rounded-xl border border-ember/30 bg-ember/5 px-4 py-3 text-sm text-ink-200">
                Connect a Facebook account in <a href="/client/mcp/connections" className="font-semibold text-brand hover:underline">MCP Connections</a> to publish Meta campaigns.
              </div>
            )}
          </div>

          {/* Publish result / error */}
          {publishError && (
            <div className="rounded-2xl border border-ember/40 bg-ember/5 p-4 text-sm text-ink">
              <div className="font-bold text-ember">Publish failed</div>
              {Array.isArray(publishError) ? (
                <ul className="mt-1 list-disc pl-5 text-ink-200">
                  {publishError.map(e => <li key={e}>{e}</li>)}
                </ul>
              ) : (
                <div className="mt-1 text-ink-200">{publishError}</div>
              )}
            </div>
          )}
          {publishResult && (
            <div className={`rounded-2xl border p-4 text-sm text-ink ${publishResult.activated ? 'border-sage/40 bg-sage/5' : 'border-sun/50 bg-sun/10'}`}>
              <div className={`font-bold ${publishResult.activated ? 'text-sage' : 'text-sun-600'}`}>
                {publishResult.activated ? 'Campaign published and live on Facebook' : 'Campaign created on Facebook, but still paused'}
              </div>
              <dl className="mt-2 space-y-1 text-ink-200">
                <div>Ad account: <span className="font-semibold text-ink">{publishResult.adAccountName || 'Facebook ad account'} ({publishResult.adAccount})</span></div>
                {publishResult.accountName && <div>Connected Facebook account: <span className="font-semibold text-ink">{publishResult.accountName}</span></div>}
                <div>Facebook campaign ID: <span className="font-semibold text-ink">{publishResult.campaign?.id}</span></div>
              </dl>
              <p className="mt-2 text-ink-200">
                {publishResult.activated
                  ? 'Meta reviews every new ad (usually within 24 hours) before it starts delivering. You can pause, resume or delete it from Audiences.'
                  : `It could not be switched on: ${publishResult.activationError}. Resume it from Audiences once fixed.`}
              </p>
              <div className="mt-3 flex flex-wrap gap-4">
                <a href="/client/mcp/audiences" className="font-semibold text-brand hover:underline">View in Audiences →</a>
                {publishResult.adsManagerUrl && (
                  <a href={publishResult.adsManagerUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-ink-200 hover:text-ink hover:underline">
                    Open in Meta Ads Manager ↗
                  </a>
                )}
              </div>
            </div>
          )}

          <div className="rounded-2xl border-2 border-dashed border-paper-300 bg-paper-50 px-6 py-8 text-center text-sm text-ink-200">
            Nothing spends until you publish. Publishing creates the campaign on your connected Facebook ad account and switches it on.
          </div>
          <button
            type="button"
            onClick={onPublish}
            disabled={publishing || !!publishResult}
            className="w-full rounded-full bg-ink px-6 py-3 text-sm font-bold text-white shadow-press transition hover:bg-ink-50 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {publishing ? 'Publishing…' : publishResult ? 'Published' : 'Publish campaign'}
          </button>
        </div>
      );
    default:
      return null;
  }
}

const McpBuild = () => {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [connections, setConnections] = useState({ facebook: false, google: false });
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [publishError, setPublishError] = useState(null);
  const [facebookPages, setFacebookPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(true);

  // Load the Pages managed by the connected Facebook account(s)
  useEffect(() => {
    let cancelled = false;
    fetch('/api/integrations/facebook/pages', { headers: authHeader() })
      .then(r => (r.ok ? r.json() : { pages: [] }))
      .then(data => { if (!cancelled) setFacebookPages(data.pages || []); })
      .catch(() => { if (!cancelled) setFacebookPages([]); })
      .finally(() => { if (!cancelled) setPagesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Check connection status on mount
  useEffect(() => {
    let cancelled = false;
    async function check() {
      try {
        const [fbRes, googleRes] = await Promise.all([
          fetch('/api/integrations/facebook/accounts', { headers: authHeader() })
            .then(r => r.ok ? r.json() : [])
            .catch(() => []),
          fetch('/api/integrations/google-ads/accounts', { headers: authHeader() })
            .then(r => r.ok ? r.json() : [])
            .catch(() => []),
        ]);
        if (cancelled) return;
        setConnections({
          facebook: Array.isArray(fbRes) ? fbRes.length > 0 : false,
          google: Array.isArray(googleRes) ? googleRes.length > 0 : false,
        });
      } catch {
        // ignore — connections stay false
      }
    }
    check();
    return () => { cancelled = true; };
  }, []);

  const steps = WIZARD_STEPS;
  const step = steps[state.stepIndex];
  const activeIndex = CHAPTERS.indexOf(step.chapter);

  const setCampaign = (c) => dispatch({ type: 'set_campaign', campaign: c });

  const handlePublish = async () => {
    const c = state.campaign;
    setPublishResult(null);

    // Validate first — previously an invalid URL crashed with "Invalid URL" and
    // missing fields were silently replaced with placeholder ad copy
    const errors = getPublishErrors(c, connections);
    if (errors.length) {
      setPublishError(errors);
      return;
    }

    setPublishing(true);
    setPublishError(null);
    try {
      const body = {
        name: c.campaignName.trim(),
        page_id: c.pageId,
        account_slot: c.accountSlot,
        objective: c.objective,
        special_ad_categories: c.specialAdCategories || [],
        country: c.sacCountry || 'US',
        age_min: c.demographics?.ageMin,
        age_max: c.demographics?.ageMax,
        budget_type: c.budget?.type,
        budget_amount_usd: c.budget?.amountUsd,
        start_time: c.schedule?.startsAt ? c.schedule.startsAt.startOf('day').toISOString() : undefined,
        end_time: c.schedule?.endsAt ? c.schedule.endsAt.endOf('day').toISOString() : undefined,
        link: c.destinationUrl.trim(),
        // Meta ads take one headline / primary text; the first filled variant is used
        headline: (c.headlines || []).find(h => h.trim()),
        primary_text: (c.primaryTexts || []).find(p => p.trim()),
        call_to_action: c.cta,
      };
      const res = await fetch('/api/ads/facebook/campaigns', {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || 'Failed to publish campaign');
      setPublishResult(data);
    } catch (err) {
      setPublishError(err.message || 'Failed to publish campaign');
    } finally {
      setPublishing(false);
    }
  };

  // Step whose Next was clicked with invalid fields. Errors are recomputed live from the
  // campaign, so each message disappears as soon as the user fixes that field.
  const [attemptedStepId, setAttemptedStepId] = useState(null);
  const stepErrors = attemptedStepId === step.id ? validateStep(step.id, state.campaign) : {};

  // Returns true when the current step is valid; otherwise shows its field errors
  const validateCurrentStep = () => {
    const errors = validateStep(step.id, state.campaign);
    if (Object.keys(errors).length > 0) {
      setAttemptedStepId(step.id);
      message.warning(Object.values(errors)[0]);
      return false;
    }
    setAttemptedStepId(null);
    return true;
  };

  const onNext = () => {
    if (!validateCurrentStep()) return;
    if (state.stepIndex < steps.length - 1) dispatch({ type: 'next' });
  };
  const onBack = () => {
    setAttemptedStepId(null);
    dispatch({ type: 'back' });
  };

  const firstVisitedStepOfChapter = (chapter) => {
    const limit = Math.min(state.maxVisitedIndex, steps.length - 1);
    for (let i = 0; i <= limit; i++) {
      if (steps[i].chapter === chapter) return i;
    }
    return null;
  };
  const clickableChapters = CHAPTERS.filter(c => firstVisitedStepOfChapter(c) !== null);
  const onChapterClick = (chapter) => {
    const target = firstVisitedStepOfChapter(chapter);
    if (target === null) return;
    // Jumping forward must not skip past an invalid step; going back is always allowed
    if (target > state.stepIndex && !validateCurrentStep()) return;
    if (target <= state.stepIndex) setAttemptedStepId(null);
    dispatch({ type: 'goto', stepIndex: target });
  };

  const isFirstStep = state.stepIndex === 0;
  const isLastStep = state.stepIndex === steps.length - 1;

  return (
    <div className="flex flex-col bg-paper" style={{ minHeight: 'calc(100vh - 64px)' }}>
      <McpHeader title="Build" />
      {/* Header bar */}
      <header className="border-b border-paper-300 bg-white px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full bg-ink text-[13px] font-bold text-white">P</span>
            <div>
              <div className="text-sm font-semibold text-ink">Build campaign</div>
              <div className="text-xs text-ink-200">
                Step {state.stepIndex + 1} of {steps.length} · {step.title}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/client/mcp/map')}
            className="inline-flex items-center gap-1.5 rounded-full border border-paper-300 px-3 py-1.5 text-xs font-semibold text-ink-200 transition hover:border-ink-300 hover:text-ink"
          >
            <X size={14} strokeWidth={1.75} />
            Save & exit
          </button>
        </div>
      </header>

      {/* Chapter progress bar */}
      <nav className="border-b border-paper-300 bg-white px-6 py-3">
        <div className="flex items-center gap-2">
          {CHAPTERS.map((c, i) => {
            const isCurrent = c === step.chapter;
            const isDone = i < activeIndex;
            const reached = isDone || isCurrent;
            const clickable = clickableChapters.includes(c);
            const inner = (
              <>
                <span className={`mb-1 flex w-fit items-center gap-1.5 ${isCurrent ? '-mx-2 rounded-full bg-paper-200 px-2' : ''}`}>
                  <span className={`inline-flex h-[18px] w-[18px] items-center justify-center rounded-full text-[10px] font-bold ${reached ? 'bg-ink text-white' : 'bg-paper-300 text-ink-200'}`}>
                    {i + 1}
                  </span>
                  <span className={`text-xs ${isCurrent ? 'font-bold text-ink' : isDone ? 'font-semibold text-ink' : 'text-ink-200'}`}>{c}</span>
                </span>
                <span className={`block h-1 rounded-full transition-colors duration-500 ${reached ? 'bg-ink' : 'bg-paper-300'}`} />
              </>
            );
            return clickable ? (
              <button key={c} type="button" onClick={() => onChapterClick(c)} className="flex-1 text-left transition hover:opacity-80">{inner}</button>
            ) : (
              <span key={c} className="block flex-1 text-left">{inner}</span>
            );
          })}
        </div>
      </nav>

      {/* Step content */}
      <main className="flex-1 bg-paper px-6 py-8">
        <div className="mx-auto w-full max-w-3xl">
          <div key={step.id} className="animate-fade-up">
            <div className="text-xs font-bold uppercase tracking-[0.14em] text-ink-200">
              Chapter {activeIndex + 1} — {step.chapter}
            </div>
            <h1 className="mt-1 font-display text-2xl font-bold text-ink">{STEP_COPY[step.id].headline}</h1>
            <p className="mt-1 text-sm text-ink-200">{STEP_COPY[step.id].subtitle}</p>
            <div className="mt-8">
              <StepContent
                step={step}
                campaign={state.campaign}
                setCampaign={setCampaign}
                errors={stepErrors}
                connections={connections}
                facebookPages={facebookPages}
                pagesLoading={pagesLoading}
                publishing={publishing}
                publishResult={publishResult}
                publishError={publishError}
                onPublish={handlePublish}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-paper-300 bg-white px-6 py-4">
        <div className="mx-auto flex w-full max-w-3xl items-center justify-between">
          <button
            type="button"
            onClick={isFirstStep ? () => navigate('/client/mcp/map') : onBack}
            className="inline-flex items-center gap-1.5 rounded-full border border-paper-300 px-4 py-2 text-sm font-semibold text-ink-100 transition hover:border-ink-300 hover:text-ink"
          >
            <ArrowLeft size={16} strokeWidth={1.75} />
            {isFirstStep ? 'Edit audience' : 'Back'}
          </button>
          {!isLastStep && (
            <button
              type="button"
              onClick={onNext}
              className="inline-flex items-center gap-1.5 rounded-full bg-ink px-6 py-2 text-sm font-bold text-white shadow-press transition hover:bg-ink-50 active:scale-[0.98]"
            >
              {step.id === 'audience' ? 'Start campaign setup' : 'Next'}
              <ArrowRight size={16} strokeWidth={1.75} />
            </button>
          )}
        </div>
      </footer>
    </div>
  );
};

export default McpBuild;
